import { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import ExerciseSessionProgressBar from "./ExerciseSessionProgressBar";
import * as s from "./Exercises.sc";
import LoadingAnimation from "../components/LoadingAnimation";
import { setTitle } from "../assorted/setTitle";
import OutOfWordsMessage from "./OutOfWordsMessage";
import SessionStorage from "../assorted/SessionStorage";
import { CORRECT, SOLUTION, WRONG } from "./ExerciseConstants";
import { EXERCISE_TYPES } from "./ExerciseTypeConstants";
import LocalStorage from "../assorted/LocalStorage";
import { pushDrillVocab } from "../assorted/drillCache";
import {
  assignBookmarksToExercises,
  assignBookmarksToA0Exercises,
  sprinkleRecallTests,
} from "./assignBookmarksToExercises";
import NextNavigation from "./exerciseTypes/NextNavigation";
import { SpeechContext } from "../contexts/SpeechContext";
import { EXTENDED_SEQUENCE, EXTENDED_SEQUENCE_NO_AUDIO } from "./exerciseSequenceTypes";
import { isA0Learner } from "../utils/misc/userCefrLevel";
import { ExercisesCounterContext } from "./ExercisesCounterContext";
import useShadowRef from "../hooks/useShadowRef";
import useExerciseSession from "../hooks/useExerciseSession";
import DigitalTimer from "../components/DigitalTimer";

import useScreenWidth from "../hooks/useScreenWidth";
import useSoftKeyboardOpen from "../hooks/useSoftKeyboardOpen";
import useUndoKeyboardScroll from "../hooks/useUndoKeyboardScroll";
import ExerciseLinksContext from "./ExerciseLinksContext";
import { NarrowColumn } from "../components/ColumnWidth.sc";
import useSubSessionTimer from "../hooks/useSubSessionTimer";
import { APIContext } from "../contexts/APIContext";
import { UserContext } from "../contexts/UserContext";
import isEmptyDictionary from "../utils/misc/isEmptyDictionary";
import WordProgressBar from "./progressBars/WordProgressBar";
import { getExerciseTypeName } from "./exerciseTypes/exerciseTypeNames";
import { useFeedbackContext } from "../contexts/FeedbackContext";
import ReportExerciseDialog from "./exerciseTypes/ReportExerciseDialog";

const BOOKMARKS_DUE_REVIEW = false;

export default function ExerciseSession({ articleID, backButtonAction, toScheduledExercises, source }) {
  const api = useContext(APIContext);
  const speech = useContext(SpeechContext);
  const { userDetails, userPreferences } = useContext(UserContext);
  // Read when the session is built, not when the page mounted: the exercises
  // are fetched after mount, and the profile may only have arrived since.
  const isA0Ref = useRef(false);
  isA0Ref.current = isA0Learner(userDetails);
  const { setContextualInfo } = useFeedbackContext();
  const history = useHistory();
  const isSoftKeyboardOpen = useSoftKeyboardOpen();
  useUndoKeyboardScroll(isSoftKeyboardOpen);

  const [hasKeptExercising, setHasKeptExercising] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentBookmarksToStudy, setCurrentBookmarksToStudy] = useState();
  // Gate on this rather than just on currentBookmarksToStudy being set, so
  // Show solution / Report never show against the loading screen (or the
  // wait-drill on a slow connection). Reset every time the exercise
  // transitions; every exercise component flips it back by calling
  // `onExerciseLoaded` (passed to it below) once it has real content.
  const [navReady, setNavReady] = useState(false);
  useEffect(() => {
    setNavReady(false);
  }, [currentBookmarksToStudy]);
  const [currentInteractiveText, setCurrentInteractiveText] = useState(null);
  const handleExerciseLoaded = (it) => {
    setNavReady(true);
    if (it) setCurrentInteractiveText(it);
  };
  // Reset interactiveText reference when bookmarks change so we don't
  // briefly use a stale one for auto-pronounce on a new exercise.
  useEffect(() => {
    setCurrentInteractiveText(null);
  }, [currentBookmarksToStudy]);
  const [correctBookmarks, setCorrectBookmarks] = useState([]);
  const [incorrectBookmarks, setIncorrectBookmarks] = useState([]);
  const [fullExerciseProgression, setFullExerciseProgression] = useState();

  const [currentExerciseType, setCurrentExerciseType] = useState(null);

  // We can use this too states to track if the user is correct.
  // isExerciseOver & !isShownSolution & isCorrect = User Correct
  // isExerciseOver & isShownSolution & isCorrect === null = User Show Solution
  // isExerciseOver & isShownSolution === null & !isCorrect = User Wrong

  const [isCorrect, setIsCorrect] = useState(null);
  const [isExerciseOver, setIsExerciseOver] = useState(false);
  const [isShowSolution, setIsShowSolution] = useState(false);
  // BottomInput has Show solution and No audio now under its input.
  const [linksUnderInput, setLinksUnderInput] = useState(false);
  const [selectedExerciseBookmark, setSelectedExerciseBookmark] = useState(null);

  const [showFeedbackButtons, setShowFeedbackButtons] = useState(false);
  const [reload, setReload] = useState(false);
  const [articleTitle, setArticleTitle] = useState();
  const [articleURL, setArticleURL] = useState();
  const [isOutOfWordsToday, setIsOutOfWordsToday] = useState();
  const [totalPracticedBookmarksInSession, setTotalPracticedBookmarksInSession] = useState(0);
  const [exerciseMessageForAPI, setExerciseMessageForAPI] = useState({});
  // What the learner typed or picked in each wrong attempt, per bookmark id.
  // A ref, not state: showSolution records and uploads in the same tick.
  const wrongAnswersRef = useRef({});
  const [isSessionLoading, setIsSessionLoading] = useState(true); // New loading state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [isReported, setIsReported] = useState(false);
  const initGenRef = useRef(0); // incremented on each init; lets async callbacks detect stale mounts
  const currentIndexRef = useShadowRef(currentIndex);
  const hasKeptExercisingRef = useShadowRef(hasKeptExercising);

  // Exercise session hook - starts when exercises are loaded
  const {
    exerciseSessionId,
    getExerciseSessionId,
    sessionDuration,
    isTimerActive,
  } = useExerciseSession(!!fullExerciseProgression);
  const sessionDurationRef = useShadowRef(sessionDuration);
  const [getCurrentSubSessionDuration, resetSubSessionTimer] = useSubSessionTimer(sessionDurationRef.current);
  const { hasExerciseNotification, decrementExerciseCounter, hideExerciseCounter } =
    useContext(ExercisesCounterContext);

  const { isMobile } = useScreenWidth();

  useEffect(() => {
    const gen = ++initGenRef.current;
    initializeExerciseSessionComponent(gen);
    return () => {
      cleanupExerciseSessionComponent();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ********************************************************************************
  // Component Initialization
  // ********************************************************************************

  function handleExerciseFocus() {
    api.logUserActivity(api.EXERCISE_FOCUSED, articleID || "", "", source || "");
  }

  function handleExerciseBlur() {
    api.logUserActivity(api.EXERCISE_UNFOCUSED, articleID || "", "", source || "");
  }

  function initializeExerciseSessionComponent(gen) {
    // Use cached preferences from UserContext instead of making an API call
    if (SessionStorage.getAudioExercisesEnabled() === undefined && userPreferences) {
      SessionStorage.setAudioExercisesEnabled(
        userPreferences["audio_exercises"] === undefined || userPreferences["audio_exercises"] === "true",
      );
    }

    api.logUserActivity(
      articleID ? api.TO_EXERCISES_AFTER_REVIEW : api.SCHEDULED_EXERCISES_OPEN,
      null,
      JSON.stringify({ had_notification: hasExerciseNotification }),
      "",
    );

    // Track focus/blur for exercise sessions
    window.addEventListener("focus", handleExerciseFocus);
    window.addEventListener("blur", handleExerciseBlur);

    // ========================================
    // GET THE BOOKMARKS THAT ARE TO BE STUDIED
    // ========================================
    resetExerciseSessionState();
    getBookmarksAndAssignThemToExercises(gen);
  }

  // The last fetched bookmarks and how the session was built from them, so it
  // can be built again if the profile arrives after them and says A0.
  const fetchedRef = useRef(null);
  useEffect(() => {
    const fetched = fetchedRef.current;
    if (!fetched || fetched.builtAsA0 === isA0Ref.current) return;
    if (currentIndexRef.current > 0) return; // already practising: leave it
    initializeExercisesForBookmarks(fetched.bookmarks, fetched.gen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isA0Ref.current]);

  function initializeExercisesForBookmarks(bookmarks, gen) {
    if (gen !== initGenRef.current) return; // stale: a newer init has superseded this one
    fetchedRef.current = { bookmarks, gen, builtAsA0: isA0Ref.current };
    if (bookmarks.length === 0) {
      setIsOutOfWordsToday(true);
      setIsSessionLoading(false); // No exercises to load
      return;
    }

    // A0: they know their words only by ear, so every exercise is listening,
    // and none asks them to type (see assignBookmarksToA0Exercises).
    let exerciseSession = isA0Ref.current
      ? assignBookmarksToA0Exercises(bookmarks)
      : sprinkleRecallTests(assignBookmarksToExercises(bookmarks, getExerciseSequenceType()));
    if (exerciseSession.length === 0) {
      setIsOutOfWordsToday(true);
      setIsSessionLoading(false);
      return;
    }
    setFullExerciseProgression(exerciseSession);

    setCurrentBookmarksToStudy(exerciseSession[0].bookmarks);
    setSelectedExerciseBookmark(exerciseSession[0].bookmarks[0]);

    // Session is now created by useExerciseSession hook when fullExerciseProgression is set
    setIsSessionLoading(false);
  }

  function resetExerciseSessionState() {
    setIsOutOfWordsToday();
    setFullExerciseProgression();
    setCurrentBookmarksToStudy();
    setCorrectBookmarks([]);
    setIncorrectBookmarks([]);
    setCurrentIndex(0);
  }

  function getBookmarksAndAssignThemToExercises(gen) {
    if (articleID) {
      // Fetch article info and words in parallel for faster loading
      const articleInfoPromise = new Promise((resolve) => {
        api.getArticleInfo(articleID, resolve);
      });
      const wordsPromise = new Promise((resolve) => {
        api.wordsToStudyForArticle(articleID, true, resolve);
      });

      Promise.all([articleInfoPromise, wordsPromise]).then(([articleInfo, bookmarks]) => {
        hideExerciseCounter(); // for article bookmarks we do not show the counter

        setArticleTitle(articleInfo.title);
        setArticleURL(articleInfo.url);
        setTitle('Exercises for "' + articleInfo.title + '"');
        initializeExercisesForBookmarks(bookmarks, gen);
      });
    } else {
      api.getBookmarksRecommendedForPractice((bookmarks) => initializeExercisesForBookmarks(bookmarks, gen));
      setTitle("Zeeguu: Vocabulary Exercises (𝄂𝄂—𝄂𝄂)", "");
    }
  }

  // ********************************************************************************
  // Component Cleanup
  // ********************************************************************************
  function cleanupExerciseSessionComponent() {
    // Remove focus/blur event listeners
    window.removeEventListener("focus", handleExerciseFocus);
    window.removeEventListener("blur", handleExerciseBlur);

    // Session end is now handled by useExerciseSession hook on unmount

    // Clear contextual info from feedback context when leaving exercise session
    setContextualInfo(null);
  }

  function handleUserAttempt(message, bookmarkToUpdate, wrongAnswer) {
    if (message === "" || !bookmarkToUpdate) return "";
    // The same answer checked twice in a row is one attempt, not two
    const answers = wrongAnswersRef.current[bookmarkToUpdate.id] || [];
    const answer = wrongAnswer?.trim();
    if (answer && answers[answers.length - 1] !== answer) {
      wrongAnswersRef.current[bookmarkToUpdate.id] = [...answers, answer];
    }
    let _newExerciseMessageToAPI = { ...exerciseMessageForAPI };
    if (!(bookmarkToUpdate.id in _newExerciseMessageToAPI)) {
      _newExerciseMessageToAPI[bookmarkToUpdate.id] = message;
    } else _newExerciseMessageToAPI[bookmarkToUpdate.id] += message;
    setExerciseMessageForAPI(_newExerciseMessageToAPI);
    return _newExerciseMessageToAPI[bookmarkToUpdate.id];
  }

  function getExerciseSequenceType() {
    return SessionStorage.isAudioExercisesEnabled() ? EXTENDED_SEQUENCE : EXTENDED_SEQUENCE_NO_AUDIO;
  }

  useEffect(() => {
    if (isOutOfWordsToday) {
      hideExerciseCounter();
    }
  }, [isOutOfWordsToday]);

  // Create shareable URL for current exercise
  const createShareableUrl = () => {
    if (!currentExerciseType) return "";

    const exerciseTypeName = getExerciseTypeName(currentExerciseType);

    // For Match exercises, include all bookmark IDs
    if (exerciseTypeName === "Match" && currentBookmarksToStudy && currentBookmarksToStudy.length > 1) {
      const bookmarkIds = currentBookmarksToStudy.map((b) => b.id).join(",");
      return `https://zeeguu.org/exercise/${exerciseTypeName}/${bookmarkIds}`;
    }

    // For single bookmark exercises
    if (!selectedExerciseBookmark) return "";
    const bookmarkId = selectedExerciseBookmark.id;

    return `https://zeeguu.org/exercise/${exerciseTypeName}/${bookmarkId}`;
  };

  // Update feedback context with current exercise URL
  useEffect(() => {
    const exerciseUrl = createShareableUrl();
    if (exerciseUrl) {
      setContextualInfo({ url: exerciseUrl });
    }
  }, [currentExerciseType, selectedExerciseBookmark, currentBookmarksToStudy, setContextualInfo]);

  if (isOutOfWordsToday) {
    return <OutOfWordsMessage />;
  }

  // Show loading screen until session is created and bookmarks are loaded
  if (!currentBookmarksToStudy || !fullExerciseProgression || isSessionLoading) {
    return <LoadingAnimation />;
  }

  function moveToNextExercise() {
    // Dismiss keyboard before changing exercises to prevent iOS viewport shift bug
    if (document.activeElement) {
      document.activeElement.blur();
    }
    speech.stopAudio();
    LocalStorage.setLastExerciseCompleteDate(new Date().toDateString());
    setIsExerciseOver(false);
    setIsShowSolution(null);
    setExerciseMessageForAPI({});
    wrongAnswersRef.current = {};
    setIsCorrect(null);
    setShowFeedbackButtons(false);
    setIsReported(false); // Reset report state for new exercise
    const newIndex = currentIndex + 1;

    if (newIndex === fullExerciseProgression.length) {
      // Navigate to the summary page with session data
      api.getCountOfBookmarksRecommendedForPractice((bookmarkCount) => {
        history.push("/practice/summary", {
          articleID,
          isOutOfWordsToday: bookmarkCount === 0,
          totalPracticedBookmarksInSession: totalPracticedBookmarksInSession,
          correctBookmarks: correctBookmarks,
          incorrectBookmarks: incorrectBookmarks,
          exerciseSessionTimer: sessionDuration,
          articleURL,
          articleTitle,
          source,
        });
      });
      return;
    }
    setCurrentBookmarksToStudy(fullExerciseProgression[newIndex].bookmarks);
    let nextBookmarkToStudy = fullExerciseProgression[newIndex].bookmarks[0];
    setSelectedExerciseBookmark(nextBookmarkToStudy);
    setCurrentIndex(newIndex);
  }

  let correctBookmarksCopy = [...correctBookmarks];

  function correctAnswerNotification(currentBookmark, endExercise = true, outcome = CORRECT) {
    if (!incorrectBookmarks.includes(currentBookmark)) {
      setTotalPracticedBookmarksInSession(totalPracticedBookmarksInSession + 1);
      let correctBookmarksIds = correctBookmarksCopy.map((b) => b.id);

      if (!correctBookmarksIds.includes(currentBookmark.id)) {
        if (hasExerciseNotification) {
          decrementExerciseCounter();
        }
      }
      correctBookmarksCopy.push(currentBookmark);
      setCorrectBookmarks(correctBookmarksCopy);
    }
    teeBookmarkToDrillCache(currentBookmark);
    if (endExercise) {
      setIsCorrect(true);
    }
    updateAPIWithExerciseComplete(outcome, currentBookmark, endExercise);
  }

  let incorrectBookmarksCopy = [...incorrectBookmarks];

  function updateAPIWithIncorrectAnswer(currentBookmark, wrongAnswer) {
    let incorrectBookmarksIds = incorrectBookmarksCopy.map((b) => b.id);
    if (!incorrectBookmarksIds.includes(currentBookmark.id)) {
      setTotalPracticedBookmarksInSession(totalPracticedBookmarksInSession + 1);
      if (currentBookmark["cooling_interval"] > 1) {
        if (hasExerciseNotification) {
          decrementExerciseCounter();
        }
      }
    }
    incorrectBookmarksCopy.push(currentBookmark);
    setIncorrectBookmarks(incorrectBookmarksCopy);
    teeBookmarkToDrillCache(currentBookmark);
    handleUserAttempt(WRONG, currentBookmark, wrongAnswer);
  }

  function teeBookmarkToDrillCache(b) {
    if (!b || !b.from || !b.to) return;
    pushDrillVocab(
      userDetails?.learned_language,
      [{ o: b.from, t: b.to }],
      "exercise",
    );
  }

  function showSolution() {
    // Currently, only match results in all bookmarks being marked as wrong.
    let bookmarksToMarkAsWrong =
      currentExerciseType === EXERCISE_TYPES.match
        ? currentBookmarksToStudy.filter((b) => !correctBookmarksCopy.includes(b))
        : [selectedExerciseBookmark];
    for (let i = 0; i < bookmarksToMarkAsWrong.length; i++) {
      let currentBookmark = bookmarksToMarkAsWrong[i];
      updateAPIWithIncorrectAnswer(currentBookmark);
      updateAPIWithExerciseComplete(SOLUTION, currentBookmark);
    }
    setIsShowSolution(true);
  }

  function updateAPIWithExerciseComplete(message, bookmark, endExercise = true) {
    let updated_message = handleUserAttempt(message, bookmark);
    if (endExercise) setIsExerciseOver(true);
    api.uploadExerciseFinalizedData(
      updated_message,
      currentExerciseType,
      getCurrentSubSessionDuration(),
      bookmark ? bookmark.user_word_id : null,
      getExerciseSessionId(),
      undefined,
      bookmark ? wrongAnswersRef.current[bookmark.id] : undefined,
    );
  }

  function disableAudio() {
    api.logUserActivity(api.AUDIO_DISABLE, "", selectedExerciseBookmark.id, "");
    moveToNextExercise();
  }

  function onWordRemovedFromExercises(reason, word_id) {
    setIsExerciseOver(true);

    if (hasExerciseNotification) {
      decrementExerciseCounter();
    }

    api.uploadExerciseFeedback(reason, currentExerciseType, 0, word_id, exerciseSessionId);

    // Auto-advance to next exercise after a short delay for toast visibility
    setTimeout(() => {
      moveToNextExercise();
    }, 500);
  }

  function handleExampleUpdate(updateData) {
    if (!updateData.updatedBookmark) {
      return;
    }

    const updatedBookmark = updateData.updatedBookmark;

    // Validate that context_tokenized exists and is properly formatted
    if (!updatedBookmark.context_tokenized || !Array.isArray(updatedBookmark.context_tokenized)) {
      return;
    }

    // Update the current exercise with the returned bookmark data
    let updatedProgression = [...fullExerciseProgression];
    const currentBookmarkIndex = updatedProgression[currentIndex].bookmarks.findIndex(
      (b) => b.id === selectedExerciseBookmark.id,
    );

    if (currentBookmarkIndex !== -1) {
      updatedProgression[currentIndex].bookmarks[currentBookmarkIndex] = updatedBookmark;
      setFullExerciseProgression(updatedProgression);
      setCurrentBookmarksToStudy(updatedProgression[currentIndex].bookmarks);
      setSelectedExerciseBookmark(updatedBookmark);
    }
  }

  function toggleShow() {
    setShowFeedbackButtons(!showFeedbackButtons);
  }

  // If user shows solution on Match without any selected bookmark, show blank progression.
  let currentMessageToAPI =
    isEmptyDictionary(exerciseMessageForAPI) || (currentExerciseType === EXERCISE_TYPES.match && isShowSolution)
      ? ""
      : exerciseMessageForAPI[selectedExerciseBookmark.id];
  const CurrentExerciseComponent = fullExerciseProgression[currentIndex].type;

  const exerciseLinks = {
    onShowSolution: showSolution,
    exerciseType: currentExerciseType,
    disableAudio,
    setIsExerciseOver,
    setLinksUnderInput,
  };

  const wordProgressBar = isExerciseOver ? (
    <WordProgressBar
      bookmark={selectedExerciseBookmark}
      message={currentMessageToAPI}
      isGreyedOutBar={selectedExerciseBookmark === undefined}
    />
  ) : null;

  return (
    <NarrowColumn>
      <s.ExercisesColumn>
        {userPreferences?.["show_reading_timer"] === "true" && (
          <div id="exerciseTopbar">
            <div id="topbarRow">
              <div style={{ display: "flex", alignItems: "center", marginLeft: "auto" }}>
                <DigitalTimer
                  sessionDuration={sessionDuration}
                  isTimerActive={isTimerActive}
                  showClock={false}
                  style={{
                    width: "3em",
                    color: "grey",
                  }}
                />
              </div>
            </div>
            <ExerciseSessionProgressBar
              index={isCorrect ? currentIndex + 1 : currentIndex}
              total={fullExerciseProgression.length}
              style={{ display: "none" }}
            />
          </div>
        )}
        <s.ExForm className={isSoftKeyboardOpen ? "softKeyboardOpen" : undefined}>
          <ExerciseLinksContext.Provider value={exerciseLinks}>
          <CurrentExerciseComponent
            key={currentIndex}
            bookmarksToStudy={currentBookmarksToStudy}
            selectedExerciseBookmark={selectedExerciseBookmark}
            setSelectedExerciseBookmark={setSelectedExerciseBookmark}
            exerciseMessageToAPI={currentMessageToAPI}
            notifyOfUserAttempt={handleUserAttempt}
            notifyCorrectAnswer={correctAnswerNotification}
            notifyIncorrectAnswer={updateAPIWithIncorrectAnswer}
            setExerciseType={setCurrentExerciseType}
            notifyExerciseCompleted={updateAPIWithExerciseComplete}
            notifyShowSolution={showSolution}
            isCorrect={isCorrect}
            isExerciseOver={isExerciseOver}
            isShowSolution={isShowSolution}
            setIsCorrect={setIsCorrect}
            setIsExerciseOver={setIsExerciseOver}
            moveToNextExercise={moveToNextExercise}
            toggleShow={toggleShow}
            reload={reload}
            setReload={setReload}
            exerciseSessionId={exerciseSessionId}
            activeSessionDuration={sessionDuration}
            resetSubSessionTimer={resetSubSessionTimer}
            bookmarkProgressBar={wordProgressBar}
            onExampleUpdated={handleExampleUpdate}
            onExerciseLoaded={handleExerciseLoaded}
          />
          </ExerciseLinksContext.Provider>
          {/* Gate the bottom nav (Show solution / Report / Next) on:
              (a) the bookmark being usably loaded — context_tokenized is
                  the signal the child exercise has the data it needs;
              (b) navReady — covers the one-tick gap between
                  setCurrentBookmarksToStudy firing and the child's
                  useEffect actually rendering real content. Without
                  this we get a brief flash of Show solution / Report
                  against the spinner during the transition. */}
          {navReady && currentBookmarksToStudy?.[0]?.context_tokenized && (
          <NextNavigation
            exerciseType={currentExerciseType}
            bookmarkMessagesToAPI={exerciseMessageForAPI}
            exerciseBookmarks={currentBookmarksToStudy}
            exerciseBookmark={currentBookmarksToStudy[0]}
            moveToNextExercise={moveToNextExercise}
            onWordRemovedFromExercises={onWordRemovedFromExercises}
            handleShowSolution={showSolution}
            toggleShow={toggleShow}
            isCorrect={isCorrect}
            isExerciseOver={isExerciseOver}
            disableAudio={disableAudio}
            setIsExerciseOver={setIsExerciseOver}
            onExampleUpdated={handleExampleUpdate}
            onReportClick={() => setReportDialogOpen(true)}
            isReported={isReported}
            interactiveText={currentInteractiveText}
            linksUnderInput={linksUnderInput}
          />
          )}
        </s.ExForm>
        {articleID && (
          <p>
            You are practicing words from: <a href={articleURL}>{articleTitle}</a>
          </p>
        )}
      </s.ExercisesColumn>

      <ReportExerciseDialog
        open={reportDialogOpen}
        onClose={(reported) => {
          setReportDialogOpen(false);
          if (reported) {
            setIsReported(true);
            // Reporting removes the word from scheduling, so the
            // pending-exercise counter has to come down too — matches
            // the cleanup that runs on "I know this word" via
            // onWordRemovedFromExercises.
            if (hasExerciseNotification) decrementExerciseCounter();
          }
        }}
        onSkip={moveToNextExercise}
        bookmarkId={selectedExerciseBookmark?.id || currentBookmarksToStudy?.[0]?.id}
        exerciseSource={getExerciseTypeName(currentExerciseType)}
        isExerciseOver={isExerciseOver}
        contextUsed={
          selectedExerciseBookmark?.context_tokenized
            ? selectedExerciseBookmark.context_tokenized.map(t => typeof t === "string" ? t : (t.text ?? "")).join("")
            : selectedExerciseBookmark?.context || currentBookmarksToStudy?.[0]?.context || ""
        }
      />
    </NarrowColumn>
  );
}
